import {
  useWSModalCustom,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSForm,
  WSInfoBox,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useCallback } from "react";
import { IWorkLog } from "@wingspanhq/payments/dist/interfaces";
import { useMutationCancelWorkLogPayable } from "../../../../query/workLog/mutations/useMutationCancelWorkLogPayable";
import { useMutationRejectWorkLog } from "../../../../query/workLog/mutations/useMutationRejectWorkLog";
import { PublicCommentField } from "../../components/PublicCommentField";

type FormData = {
  comment: string;
};

type Props = {
  workLog: IWorkLog;
  contractorName?: string;
} & WSModalCustomProps<{
  success?: boolean;
}>;

export const ModalCancelWorkLogInvoice: React.FC<Props> = ({
  workLog,
  contractorName = "the contractor",
  ...modalProps
}) => {
  const { openSnackbar } = useWSSnackbar();
  const [cancelPayable, cancelMeta] = useMutationCancelWorkLogPayable();
  const [rejectWorkLog, rejectMeta] = useMutationRejectWorkLog();

  const isLoading = cancelMeta.isLoading || rejectMeta.isLoading;

  const onError = useCallback(() => {
    openSnackbar({
      type: "warning",
      message: "Failed to cancel invoice. Please try again."
    });
  }, [openSnackbar]);

  const handleCancel = useCallback(
    async (formData: FormData) => {
      if (!formData.comment.trim()) {
        openSnackbar({
          type: "warning",
          message: "Please provide a comment for the cancellation."
        });
        return;
      }

      if (!workLog.invoiceId) {
        openSnackbar({
          type: "warning",
          message: "No linked invoice found for this work log."
        });
        return;
      }

      // Step 1: Cancel the payable (worklog moves to InReview)
      cancelPayable(
        { invoiceId: workLog.invoiceId },
        {
          onError,
          onSuccess: () => {
            // Step 2: Reject the worklog with comment
            rejectWorkLog(
              {
                workLogId: workLog.workLogId,
                comment: formData.comment
              },
              {
                onSuccess: () => {
                  modalProps.onClose({ success: true });
                  openSnackbar({
                    icon: { name: "check" },
                    message: "Invoice cancelled successfully."
                  });
                },
                onError
              }
            );
          }
        }
      );
    },
    [
      openSnackbar,
      cancelPayable,
      rejectWorkLog,
      onError,
      workLog.invoiceId,
      workLog.workLogId,
      modalProps
    ]
  );

  return (
    <WSModal title="Cancel invoice" {...modalProps}>
      <WSForm<FormData> onSubmit={handleCancel} defaultValues={{ comment: "" }}>
        <WSList gap="XL">
          <WSText.ParagraphSm color="gray500">
            Are you sure you want to cancel the invoice?
          </WSText.ParagraphSm>

          <WSInfoBox title="Invoices funded for payment">
            If an invoice has been funded for payment already, the funds are
            returned to the payroll funding account when the invoice is
            cancelled.
          </WSInfoBox>

          <PublicCommentField
            label="Comment *"
            description={`Comment is visible to both you and the contractor. Please provide specific details that will inform ${contractorName} about the decision.`}
            placeholder="Provide specific details about the decision"
          />

          <WSAlert theme="warning" title="Cancel invoice" icon="alert">
            This action cannot be undone
          </WSAlert>

          <WSActions
            alignment="fill"
            buttons={[
              {
                kind: "Secondary",
                label: "Cancel",
                type: "button",
                onClick: () => {
                  modalProps.onClose({ success: false });
                }
              },
              {
                kind: "Primary",
                destructive: true,
                label: "Cancel invoice",
                loading: isLoading
              }
            ]}
          />
        </WSList>
      </WSForm>
    </WSModal>
  );
};

export const useModalCancelWorkLogInvoice = () =>
  useWSModalCustom(ModalCancelWorkLogInvoice);
